/**
 * Zapiet API Integration for Delivery Availability
 * 
 * This file handles all Zapiet API interactions including:
 * - Fetching delivery locations
 * - Fetching calendar rules and available dates
 * - Generating date options based on calendar rules
 * - Managing time slots
 * - Updating SparkLayer checkout fields dynamically
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

const ZAPIET_CONFIG = {
  shop: "sgbarkery.myshopify.com",
  method: "delivery", // "delivery" or "pickup"
  locationId: window.ZAPIET_DELIVERY_LOCATION_ID || "12549"
};

const ZAPIET_DEBUG = false;

function zapietLog() {
  if (ZAPIET_DEBUG) console.log.apply(console, arguments);
}

function zapietWarn() {
  if (ZAPIET_DEBUG) console.warn.apply(console, arguments);
}


// ============================================================================
// GLOBAL STATE
// ============================================================================

window.zapietLocations = window.zapietLocations || [];
window.zapietCachedLocationByLocationId = window.zapietCachedLocationByLocationId || {};

let zapietDeliveryDates = [
  {
    translations: { en: { groupname: "Loading..." } },
    items: [{ value: "", translations: { en: { label: "Loading available dates..." } } }]
  }
];

let zapietDatesLoading = false;

var zapietLocationsOptions = [];

var zapietSlotOptions = [
  { translations: { en: { groupname: "Time slot" } }, items: [{ value: "", translations: { en: { label: "Select a time slot" } } }] }
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getTodayDateString() {
  var d = new Date();
  var y = d.getFullYear();
  var m = String(d.getMonth() + 1).padStart(2, "0");
  var day = String(d.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + day;
}

function getEffectiveLocationId() {
  return window.ZAPIET_DELIVERY_LOCATION_ID || 
         (window.zapietLocations && window.zapietLocations[0] && String(window.zapietLocations[0].id)) || 
         null;
}

function cartToZapietShoppingCart(cart) {
  return (cart.items || []).map(i => ({
    variant_id: i.variant_id,
    product_id: i.product_id,
    quantity: i.quantity
  }));
}

// ============================================================================
// LOCATION FUNCTIONS
// ============================================================================

function getZapietLocationsOptions() {
  var locs = window.zapietLocations || [];
  console.log("📍 getZapietLocationsOptions called, locations:", locs);
  
  function label(loc) {
    var parts = [loc.company_name || "Location"];
    if (loc.address_line_1) parts.push(loc.address_line_1);
    var cityLine = [loc.city, loc.postal_code].filter(Boolean).join(" ");
    if (cityLine) parts.push(cityLine);
    if (loc.country) parts.push(loc.country);
    return parts.join(", ");
  }
  
  var items = locs.map(function(loc) {
    return { value: String(loc.id), translations: { en: { label: label(loc) } } };
  });
  
  if (locs.length > 1) {
    items = [{ value: "", translations: { en: { label: "Select location" } } }].concat(items);
  }
  
  var result = [{ translations: { en: { groupname: "Delivery location" } }, items: items }];
  console.log("📍 Location options generated:", result);
  return result;
}

async function fetchZapietLocations() {
  var method = ZAPIET_CONFIG.method || "delivery";
  var url = "https://api.zapiet.com/v1.0/" + method + "/locations?shop=" + encodeURIComponent(ZAPIET_CONFIG.shop);
  console.log("🌐 Fetching Zapiet locations from:", url);
  
  try {
    var response = await fetch(url, { method: "GET" });
    if (!response.ok) {
      console.error("❌ Zapiet locations API error:", response.status, response.statusText);
      var errorText = await response.text().catch(function() { return ""; });
      console.error("❌ Response body:", errorText);
      return [];
    }
    
    var data = await response.json();
    console.log("✅ Zapiet locations fetched successfully:", data);
    
    // Handle both array and object responses
    var locations = [];
    if (Array.isArray(data)) {
      locations = data;
    } else if (data.locations && Array.isArray(data.locations)) {
      locations = data.locations;
    } else if (data.data && Array.isArray(data.data)) {
      locations = data.data;
    } else if (data.id) {
      // Single location object - wrap in array
      locations = [data];
      console.log("📍 Single location object detected, wrapped in array");
    }
    
    console.log("📍 Processed locations:", locations);
    return locations;
  } catch (e) {
    console.error("❌ Error fetching Zapiet locations:", e);
    console.error("❌ Error details:", e.message, e.stack);
    return [];
  }
}

async function fetchZapietLocation(locationId) {
  if (!locationId) {
    console.warn("fetchZapietLocation: No locationId provided");
    return null;
  }
  var method = ZAPIET_CONFIG.method || "delivery";
  var url = "https://api.zapiet.com/v1.0/" + method + "/locations/" + locationId + "?shop=" + encodeURIComponent(ZAPIET_CONFIG.shop);
  console.log("🌐 Fetching Zapiet location from:", url);
  
  try {
    var response = await fetch(url, { method: "GET" });
    if (!response.ok) {
      console.error("❌ Zapiet location API error:", response.status, response.statusText);
      var errorText = await response.text().catch(function() { return ""; });
      console.error("❌ Response body:", errorText);
      return null;
    }
    var data = await response.json();
    console.log("✅ Zapiet location fetched successfully:", data);
    return data;
  } catch (e) {
    console.error("❌ Error fetching Zapiet location:", e);
    console.error("❌ Error details:", e.message, e.stack);
    return null;
  }
}

// ============================================================================
// CALENDAR & DATE FUNCTIONS
// ============================================================================

async function fetchZapietCalendarRules(cart, locationId) {
  if (!locationId) {
    console.warn("Zapiet: No location ID provided");
    return null;
  }

  const shoppingCart = cartToZapietShoppingCart(cart);
  const url = ZAPIET_CONFIG.method === "delivery"
    ? `https://api.zapiet.com/v1.0/delivery/locations/${locationId}/calendar?shop=${encodeURIComponent(ZAPIET_CONFIG.shop)}`
    : `https://api.zapiet.com/v1.0/pickup/locations/${locationId}/calendar?shop=${encodeURIComponent(ZAPIET_CONFIG.shop)}`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shoppingCart })
    });

    if (!response.ok) {
      throw new Error(`Zapiet calendar rules fetch failed: ${response.status}`);
    }

    const data = await response.json();
    console.log("Zapiet calendar API response:", data);
    
    // If the calendar API returns full location data with delivery object, cache it
    if (data && data.delivery && data.id) {
      window.zapietCachedLocationByLocationId = window.zapietCachedLocationByLocationId || {};
      var locationIdKey = String(locationId);
      var responseIdKey = String(data.id);
      window.zapietCachedLocationByLocationId[locationIdKey] = data;
      if (responseIdKey !== locationIdKey) {
        console.log("📦 Caching calendar response location with both IDs:", locationIdKey, "and", responseIdKey);
        window.zapietCachedLocationByLocationId[responseIdKey] = data;
      }
      console.log("📦 Calendar response location cached");
    }
    
    return data;
  } catch (error) {
    console.error("Error fetching Zapiet calendar rules:", error);
    return null;
  }
}

function generateAvailableDates(calendarRules) {
  var emptyResult = { dates: [], enabledDaysOfWeek: [], enabledDayNumbers: [], disabledDateStrings: [], disabledDayNumbers: [] };
  if (!calendarRules) {
    console.warn("Zapiet: No calendar rules provided");
    return emptyResult;
  }

  // Handle if response is an array (multiple locations)
  if (Array.isArray(calendarRules)) {
    if (calendarRules.length === 0) {
      console.warn("Zapiet: Empty calendar rules array");
      return emptyResult;
    }
    calendarRules = calendarRules[0];
  }

  console.log("📅 Processing calendar rules:", {
    minDate: calendarRules.minDate,
    maxDate: calendarRules.maxDate,
    disabled: (calendarRules.disabled && calendarRules.disabled.length) || 0,
    slots: (calendarRules.slots && calendarRules.slots.length) || 0
  });

  // Check if this is the calendar rules format (has minDate, maxDate, disabled)
  if (!calendarRules.minDate || !calendarRules.maxDate) {
    if (calendarRules.delivery) {
      console.warn("Zapiet: Received location data format instead of calendar rules");
      return { dates: [], enabledDaysOfWeek: [], enabledDayNumbers: [], disabledDateStrings: [], disabledDayNumbers: [] };
    }
    console.warn("Zapiet: Invalid calendar rules format", calendarRules);
    return emptyResult;
  }

  const dates = [];
  var todayStr = getTodayDateString();
  var rangeMin = calendarRules.minDate;
  if (!rangeMin || rangeMin < todayStr) {
    rangeMin = todayStr;
  }
  const minDate = new Date(rangeMin + "T00:00:00");
  const maxDate = new Date(calendarRules.maxDate + "T23:59:59");
  const disabled = calendarRules.disabled || [];
  const slots = calendarRules.slots || [];
  const minDateSlots = calendarRules.minDateSlots || [];
  
  // Extract day of week names from slots
  const enabledDaysOfWeek = new Set();
  slots.forEach(slot => {
    if (slot.day_of_week) {
      enabledDaysOfWeek.add(slot.day_of_week.toLowerCase());
    }
  });
  
  if (Array.isArray(minDateSlots) && minDateSlots.length > 0) {
    minDateSlots.forEach(slot => {
      if (slot.day_of_week) {
        enabledDaysOfWeek.add(slot.day_of_week.toLowerCase());
      }
    });
  }
  
  // Day name to day number mapping
  const dayNameToNumber = {
    'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
    'thursday': 4, 'friday': 5, 'saturday': 6
  };
  
  const enabledDayNumbers = new Set();
  enabledDaysOfWeek.forEach(dayName => {
    if (dayNameToNumber[dayName] !== undefined) {
      enabledDayNumbers.add(dayNameToNumber[dayName]);
    }
  });
  
  // Parse disabled dates
  const disabledDateStrings = new Set();
  const disabledDayNumbers = new Set();
  
  disabled.forEach(item => {
    if (Array.isArray(item) && item.length === 3) {
      const [year, month, day] = item;
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      disabledDateStrings.add(dateStr);
    } else if (typeof item === 'number') {
      disabledDayNumbers.add(item);
    }
  });
  
  console.log("📅 Enabled days of week:", Array.from(enabledDaysOfWeek));
  console.log("📅 Enabled day numbers:", Array.from(enabledDayNumbers));
  console.log("📅 Disabled date strings:", Array.from(disabledDateStrings));
  console.log("📅 Disabled day numbers:", Array.from(disabledDayNumbers));
  
  function formatDate(date) {
    function pad(n) {
      return String(n).padStart(2, "0");
    }
    return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate());
  }

  try {
    const currentDate = new Date(minDate);
    currentDate.setHours(0, 0, 0, 0);
    const endDate = new Date(maxDate);
    endDate.setHours(23, 59, 59, 999);

    while (currentDate <= endDate) {
      const dateStr = formatDate(currentDate);
      const dayOfWeek = currentDate.getDay();
      
      if (disabledDateStrings.has(dateStr)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      
      if (disabledDayNumbers.has(dayOfWeek)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }
      
      if (enabledDayNumbers.size > 0) {
        if (enabledDayNumbers.has(dayOfWeek)) {
          dates.push({
            value: dateStr,
            label: currentDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
          });
        }
      } else {
        dates.push({
          value: dateStr,
          label: currentDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log("📅 Generated", dates.length, "available dates");
  } catch (error) {
    console.error("❌ Error generating available dates:", error);
    return emptyResult;
  }

  return {
    dates: Array.isArray(dates) ? dates : [],
    enabledDaysOfWeek: Array.from(enabledDaysOfWeek),
    enabledDayNumbers: Array.from(enabledDayNumbers),
    disabledDateStrings: Array.from(disabledDateStrings),
    disabledDayNumbers: Array.from(disabledDayNumbers)
  };
}

// ============================================================================
// TIME SLOT FUNCTIONS
// ============================================================================

async function fetchZapietTimeSlots(locationId, dateYmd) {
  if (!locationId || !dateYmd) return null;
  var url = ZAPIET_CONFIG.method === "delivery"
    ? "https://api.zapiet.com/v1.0/delivery/locations/" + locationId + "/calendar/" + dateYmd + "?shop=" + encodeURIComponent(ZAPIET_CONFIG.shop)
    : "https://api.zapiet.com/v1.0/pickup/locations/" + locationId + "/calendar/" + dateYmd + "?shop=" + encodeURIComponent(ZAPIET_CONFIG.shop);

  try {
    var response = await fetch(url, {
      method: ZAPIET_CONFIG.method === "delivery" ? "GET" : "POST"
    });

    if (!response.ok) {
      throw new Error("Zapiet times fetch failed: " + response.status);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching Zapiet time slots:", error);
    return null;
  }
}

function getTimeSlotLabelFromResponse(apiResponse) {
  if (!apiResponse) return "";
  var slots = apiResponse.slots || apiResponse.time_slots || apiResponse.times || (Array.isArray(apiResponse) ? apiResponse : []);
  if (!Array.isArray(slots) || slots.length === 0) return "";
  
  function formatTime(str) {
    if (!str) return "";
    var s = String(str);
    var parts = s.split(":");
    var h = parseInt(parts[0], 10);
    if (isNaN(h)) return s;
    var m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    if (isNaN(m)) m = 0;
    var ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return h + ":" + String(m).padStart(2, "0") + " " + ampm;
  }
  
  var labels = [];
  for (var i = 0; i < slots.length; i++) {
    var slot = slots[i];
    var from = slot.from || slot.start_time || slot.start || "";
    var until = slot.until || slot.end_time || slot.end || "";
    labels.push(formatTime(from) + (until ? " - " + formatTime(until) : formatTime(from) || ""));
  }
  return labels.filter(Boolean).join(", ");
}

/** Parse "HH:MM" or "HH:mm" to minutes since midnight */
function slotTimeToMinutes(str) {
  if (!str) return 0;
  var parts = String(str).trim().split(":");
  var h = parseInt(parts[0], 10);
  var m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
  if (isNaN(h)) h = 0;
  if (isNaN(m)) m = 0;
  return h * 60 + m;
}

/** Get duration of a slot in minutes (from -> until) */
function getSlotDurationMinutes(slot) {
  var from = slot.from || slot.start_time || slot.start || "";
  var until = slot.until || slot.end_time || slot.end || "";
  var startMin = slotTimeToMinutes(from);
  var endMin = slotTimeToMinutes(until);
  if (endMin <= startMin) return 0;
  return endMin - startMin;
}

/** Collect all slots from location (all days), then return the one with longest duration */
function getLongestDurationSlotFromLocation(location) {
  if (!location || !location.delivery) {
    console.warn("⚠️ getLongestDurationSlotFromLocation: No location or delivery object");
    return null;
  }
  var days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  var allSlots = [];
  for (var d = 0; d < days.length; d++) {
    var day = days[d];
    var dayConfig = location.delivery[day];
    if (!dayConfig || !dayConfig.slots || !dayConfig.slots.enabled) continue;
    var slots = dayConfig.slots.slots;
    if (!Array.isArray(slots) || slots.length === 0) continue;
    for (var i = 0; i < slots.length; i++) {
      allSlots.push(slots[i]);
    }
  }
  if (allSlots.length === 0) {
    console.warn("⚠️ No enabled slots found in location");
    return null;
  }
  var longest = allSlots[0];
  var longestMinutes = getSlotDurationMinutes(longest);
  for (var j = 1; j < allSlots.length; j++) {
    var minutes = getSlotDurationMinutes(allSlots[j]);
    if (minutes > longestMinutes) {
      longest = allSlots[j];
      longestMinutes = minutes;
    }
  }
  console.log("✅ Longest duration slot:", longest, "duration minutes:", longestMinutes);
  return longest;
}

function getFirstSlotFromLocation(location) {
  return getLongestDurationSlotFromLocation(location);
}

function getSlotOptionsFromFirstSlot(slot) {
  if (!slot) return { items: [{ value: "", translations: { en: { label: "No slot available" } } }], firstValue: "" };
  
  function formatTime(str) {
    if (!str) return "";
    var s = String(str);
    var parts = s.split(":");
    var h = parseInt(parts[0], 10);
    if (isNaN(h)) return s;
    var m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    if (isNaN(m)) m = 0;
    var ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return h + ":" + String(m).padStart(2, "0") + " " + ampm;
  }
  
  var from = slot.from || slot.start_time || slot.start || "";
  var until = slot.until || slot.end_time || slot.end || "";
  var label = formatTime(from) + (until ? " - " + formatTime(until) : formatTime(from) || "Delivery slot");
  var value = slot.id != null ? String(slot.id) : (from && until ? from + "-" + until : "slot-0");
  var items = [
    { value: value, translations: { en: { label: label } } }
  ];
  return { items: items, firstValue: value };
}

// ============================================================================
// DOM UPDATE FUNCTIONS
// ============================================================================

function findDeliverySlotSelect(root) {
  if (!root) return null;
  return root.querySelector("select[name*='Delivery-Slot-Id'], select[name*='delivery_slot_id'], select[id*='Delivery-Slot-Id'], [data-field-name='Delivery-Slot-Id'] select");
}

function findDeliveryLocationSelect(root) {
  if (!root) return null;
  return root.querySelector("select[name*='Delivery-Location-Id'], select[name*='Delivery-Location-Id' i], select[name*='delivery_location_id'], select[id*='Delivery-Location-Id'], [data-field-name='Delivery-Location-Id'] select");
}

function updateDeliverySlotSelectOptions(items, defaultValue) {
  console.log("🔄 updateDeliverySlotSelectOptions called with items:", items, "defaultValue:", defaultValue);
  
  function run(r) {
    var select = findDeliverySlotSelect(r);
    if (!select) {
      console.log("  ⚠️ Select element not found in root:", r === document ? "document" : r);
      return;
    }
    console.log("  ✅ Found select element, updating options");
    select.innerHTML = "";
    for (var i = 0; i < items.length; i++) {
      var opt = (r.ownerDocument || document).createElement("option");
      opt.value = items[i].value;
      opt.textContent = items[i].translations && items[i].translations.en && items[i].translations.en.label ? items[i].translations.en.label : items[i].value;
      select.appendChild(opt);
      console.log("    Added option:", opt.value, "-", opt.textContent);
    }
    if (defaultValue && items.some(function(o) { return o.value === defaultValue; })) {
      select.value = defaultValue;
      console.log("  ✅ Set default value to:", defaultValue);
    } else if (items[0] && items[0].value === "") {
      select.value = "";
      console.log("  ✅ Set value to empty string");
    }
    select.dispatchEvent(new Event("change", { bubbles: true }));
    console.log("  ✅ Dispatched change event");
  }
  
  run(document);
  var hosts = document.querySelectorAll("spark-cart, spark-checkout, [id*='spark'], [class*='spark-cart'], [class*='spark-checkout']");
  console.log("  Found", hosts.length, "SparkLayer host elements");
  for (var h = 0; h < hosts.length; h++) {
    if (hosts[h].shadowRoot) {
      console.log("  Checking shadow root of host", h);
      run(hosts[h].shadowRoot);
    }
  }
  setTimeout(function() { console.log("  🔄 Retry update after 200ms"); run(document); }, 200);
  setTimeout(function() { console.log("  🔄 Retry update after 600ms"); run(document); }, 600);
}

function updateDeliveryLocationSelectOptions() {
  console.log("🔄 updateDeliveryLocationSelectOptions called");
  var options = getZapietLocationsOptions();
  var items = options[0] && options[0].items ? options[0].items : [];
  console.log("📍 Location items to add:", items);
  console.log("📍 window.zapietLocations:", window.zapietLocations);
  
  // Update SparkLayer config options array directly
  if (window.sparkOptions && window.sparkOptions.checkoutCustomElements) {
    for (var i = 0; i < window.sparkOptions.checkoutCustomElements.length; i++) {
      if (window.sparkOptions.checkoutCustomElements[i].name === "Delivery-Location-Id") {
        window.sparkOptions.checkoutCustomElements[i].options = options;
        console.log("✅ Updated SparkLayer config options for Delivery-Location-Id");
        break;
      }
    }
  }
  
  function run(r) {
    var select = findDeliveryLocationSelect(r);
    if (!select) {
      console.log("  ⚠️ Location select element not found in root:", r === document ? "document" : r);
      return false;
    }
    console.log("  ✅ Found location select element, updating options");
    var currentValue = select.value;
    select.innerHTML = "";
    for (var i = 0; i < items.length; i++) {
      var opt = (r.ownerDocument || document).createElement("option");
      opt.value = items[i].value;
      opt.textContent = items[i].translations && items[i].translations.en && items[i].translations.en.label ? items[i].translations.en.label : items[i].value;
      select.appendChild(opt);
      console.log("    Added option:", opt.value, "-", opt.textContent);
    }
    if (currentValue && items.some(function(o) { return o.value === currentValue; })) {
      select.value = currentValue;
      console.log("  ✅ Restored selection:", currentValue);
    } else if (items.length === 1 && items[0].value) {
      select.value = items[0].value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      console.log("  ✅ Auto-selected single location:", items[0].value);
    }
    return true;
  }
  
  var found = run(document);
  var hosts = document.querySelectorAll("spark-cart, spark-checkout, [id*='spark'], [class*='spark-cart'], [class*='spark-checkout']");
  console.log("  Found", hosts.length, "SparkLayer host elements");
  for (var h = 0; h < hosts.length; h++) {
    if (hosts[h].shadowRoot) {
      console.log("  Checking shadow root of host", h);
      if (run(hosts[h].shadowRoot)) found = true;
    }
  }
  
  if (!found) {
    setTimeout(function() { console.log("  🔄 Retry update after 500ms"); run(document); }, 500);
    setTimeout(function() { console.log("  🔄 Retry update after 1000ms"); run(document); }, 1000);
    setTimeout(function() { console.log("  🔄 Retry update after 2000ms"); run(document); }, 2000);
    setTimeout(function() { console.log("  🔄 Retry update after 3000ms"); run(document); }, 3000);
  } else {
    setTimeout(function() { console.log("  🔄 Retry update after 200ms"); run(document); }, 200);
    setTimeout(function() { console.log("  🔄 Retry update after 600ms"); run(document); }, 600);
  }
}

function updateDeliveryDateSelectOptions(items) {
  var fullItems = items;
  function run(root) {
    if (!root) return;
    var select = root.querySelector("select[name*='Delivery-Date'], select[name*='Delivery-Date' i], [data-field-name='Delivery-Date'] select");
    if (!select) return;
    var currentValue = select.value;
    select.innerHTML = "";
    for (var i = 0; i < fullItems.length; i++) {
      var opt = root.ownerDocument.createElement("option");
      opt.value = fullItems[i].value;
      opt.textContent = fullItems[i].translations && fullItems[i].translations.en ? fullItems[i].translations.en.label : fullItems[i].value;
      select.appendChild(opt);
    }
    select.value = "";
    if (select.value !== currentValue) {
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
  run(document);
  var hosts = document.querySelectorAll("spark-cart, spark-checkout, [id*='spark'], [class*='spark-cart'], [class*='spark-checkout']");
  for (var h = 0; h < hosts.length; h++) {
    if (hosts[h].shadowRoot) run(hosts[h].shadowRoot);
  }
}

function showStaticDeliveryTime(label) {
  var id = "zapiet-delivery-time-static";
  function run(root) {
    if (!root) return;
    var dateSelect = root.querySelector("select[name*='Delivery-Date'], select[name*='Delivery-Date' i], [data-field-name='Delivery-Date'] select");
    if (!dateSelect) return;
    var container = dateSelect.closest("div, fieldset, [class*='field']") || dateSelect.parentElement;
    if (!container) return;
    var doc = container.ownerDocument || document;
    var el = root.getElementById ? root.getElementById(id) : root.querySelector("#" + id);
    if (!el) {
      el = doc.createElement("div");
      el.id = id;
      el.setAttribute("aria-live", "polite");
      el.style.marginTop = "0.25rem";
      el.style.fontSize = "0.9em";
      el.style.color = "#555";
      if (container.nextSibling) {
        container.parentNode.insertBefore(el, container.nextSibling);
      } else {
        container.appendChild(el);
      }
    }
    el.textContent = label ? "Delivery window: " + label : "";
    el.style.display = label ? "" : "none";
  }
  run(document);
  var hosts = document.querySelectorAll("spark-cart, spark-checkout, [id*='spark'], [class*='spark-cart'], [class*='spark-checkout']");
  for (var h = 0; h < hosts.length; h++) {
    if (hosts[h].shadowRoot) run(hosts[h].shadowRoot);
  }
  setTimeout(function() { run(document); }, 200);
  setTimeout(function() { run(document); }, 600);
}

function preselectFirstLocationIfOne() {
  var locs = window.zapietLocations;
  if (!locs || locs.length !== 1) return;
  var firstId = String(locs[0].id);
  function run(root) {
    if (!root) return;
    var select = findDeliveryLocationSelect(root);
    if (!select) return;
    if (select.value !== firstId) { 
      select.value = firstId; 
      select.dispatchEvent(new Event("change", { bubbles: true })); 
    }
  }
  run(document);
  var hosts = document.querySelectorAll("spark-cart, spark-checkout, [id*='spark'], [class*='spark-cart'], [class*='spark-checkout']");
  for (var h = 0; h < hosts.length; h++) {
    if (hosts[h].shadowRoot) run(hosts[h].shadowRoot);
  }
}

// ============================================================================
// SPARKLAYER SDK INTEGRATION FUNCTIONS
// ============================================================================

function updateDeliveryDateFieldOptions(dateItems) {
  console.log("🔄 updateDeliveryDateFieldOptions called with items:", dateItems);
  
  // Update the SparkLayer config options array
  if (window.sparkOptions && window.sparkOptions.checkoutCustomElements) {
    for (var i = 0; i < window.sparkOptions.checkoutCustomElements.length; i++) {
      if (window.sparkOptions.checkoutCustomElements[i].name === "Delivery-Date") {
        var formattedOptions = [{
          translations: { en: { groupname: "Available Dates" } },
          items: dateItems
        }];
        window.sparkOptions.checkoutCustomElements[i].options = formattedOptions;
        console.log("✅ Updated SparkLayer config options for Delivery-Date");
        break;
      }
    }
  }
  
  // Also update the shared array
  zapietDeliveryDates.length = 0;
  zapietDeliveryDates.push({
    translations: { en: { groupname: "Available Dates" } },
    items: dateItems
  });
  
  // Update DOM
  updateDeliveryDateSelectOptions(dateItems);
}

function updateDeliverySlotFieldOptions(slotItems) {
  console.log("🔄 updateDeliverySlotFieldOptions called with items:", slotItems);
  
  // Update the SparkLayer config options array
  if (window.sparkOptions && window.sparkOptions.checkoutCustomElements) {
    for (var i = 0; i < window.sparkOptions.checkoutCustomElements.length; i++) {
      if (window.sparkOptions.checkoutCustomElements[i].name === "Delivery-Slot-Id") {
        var formattedOptions = [{
          translations: { en: { groupname: "Time slot" } },
          items: slotItems
        }];
        window.sparkOptions.checkoutCustomElements[i].options = formattedOptions;
        console.log("✅ Updated SparkLayer config options for Delivery-Slot-Id");
        break;
      }
    }
  }
  
  // Also update the shared array
  zapietSlotOptions[0].items = slotItems;
  
  // Update DOM
  updateDeliverySlotSelectOptions(slotItems, slotItems[0] && slotItems[0].value ? slotItems[0].value : "");
}

function updateSlotFromLocation(locationId, locationDataFromCalendar) {
  console.log("🔍 updateSlotFromLocation called with locationId:", locationId, "locationDataFromCalendar:", locationDataFromCalendar);
  
  function setSlotOptions(items, defaultValue) {
    console.log("📝 Setting slot options:", items, "defaultValue:", defaultValue);
    zapietSlotOptions[0].items = items;
    updateDeliverySlotSelectOptions(items, defaultValue);
  }
  
  if (!locationId) {
    console.warn("⚠️ No locationId provided to updateSlotFromLocation");
    setSlotOptions([{ value: "", translations: { en: { label: "Select a location first" } } }], "");
    return;
  }
  
  var loc = null;
  if (locationDataFromCalendar && locationDataFromCalendar.delivery) {
    console.log("✅ Using locationDataFromCalendar");
    loc = locationDataFromCalendar;
  }
  if (!loc && window.zapietCachedLocationByLocationId[String(locationId)] && window.zapietCachedLocationByLocationId[String(locationId)].delivery) {
    console.log("✅ Using cached location data");
    loc = window.zapietCachedLocationByLocationId[String(locationId)];
  }
  if (!loc && window.zapietLocations && Array.isArray(window.zapietLocations)) {
    console.log("🔍 Checking window.zapietLocations for locationId:", locationId);
    for (var i = 0; i < window.zapietLocations.length; i++) {
      console.log("  Checking location:", window.zapietLocations[i].id, "has delivery:", !!window.zapietLocations[i].delivery);
      if (String(window.zapietLocations[i].id) === String(locationId) && window.zapietLocations[i].delivery) {
        console.log("✅ Found location in window.zapietLocations");
        loc = window.zapietLocations[i];
        break;
      }
    }
  }
  
  if (loc) {
    console.log("✅ Location found, extracting slot from:", loc);
    var slot = getFirstSlotFromLocation(loc);
    console.log("📅 First slot found:", slot);
    var data = getSlotOptionsFromFirstSlot(slot);
    console.log("📋 Slot options data:", data);
    updateDeliverySlotFieldOptions(data.items);
    setSlotOptions(data.items, data.firstValue);
    return;
  }
  
  console.log("⏳ Location not found in cache, fetching from API...");
  setSlotOptions([{ value: "", translations: { en: { label: "Loading..." } } }], "");
  fetchZapietLocation(locationId).then(function(location) {
    console.log("✅ fetchZapietLocation returned:", location);
    if (location && location.delivery) {
      var cacheKey = String(locationId);
      var responseId = location.id ? String(location.id) : null;
      window.zapietCachedLocationByLocationId[cacheKey] = location;
      if (responseId && responseId !== cacheKey) {
        console.log("📦 Caching location with both IDs:", cacheKey, "and", responseId);
        window.zapietCachedLocationByLocationId[responseId] = location;
      }
      console.log("📦 Location cached with key:", cacheKey);
    }
    var slot = getFirstSlotFromLocation(location);
    console.log("📅 Slot extracted from fetched location:", slot);
    var data = getSlotOptionsFromFirstSlot(slot);
    console.log("📋 Slot options from fetched location:", data);
    updateDeliverySlotFieldOptions(data.items);
    setSlotOptions(data.items, data.firstValue);
  }).catch(function(error) {
    console.error("❌ Error fetching location:", error);
    setSlotOptions([{ value: "", translations: { en: { label: "Select a location first" } } }], "");
  });
}

function refetchZapietDatesForLocation(locationId, callback) {
  if (!locationId) return;
  zapietDatesLoading = true;
  fetch("/cart.js")
    .then(function(r) { return r.ok ? r.json() : Promise.reject(new Error("Cart fetch failed")); })
    .then(function(cart) {
      return fetchZapietCalendarRules(cart, locationId).then(function(locationData) {
        if (!locationData) return Promise.reject(new Error("No calendar data"));
        var dateResult = generateAvailableDates(locationData);
        var availableDates = dateResult.dates || [];
        if (availableDates.length === 0) return Promise.reject(new Error("No dates"));
        var todayStr = getTodayDateString();
        var effectiveMinDate = locationData.minDate;
        if (!effectiveMinDate || effectiveMinDate < todayStr) effectiveMinDate = todayStr;
        window.zapietCalendarRules = {
          minDate: effectiveMinDate,
          maxDate: locationData.maxDate,
          disabled: locationData.disabled || [],
          enabledDaysOfWeek: dateResult.enabledDaysOfWeek || [],
          enabledDayNumbers: dateResult.enabledDayNumbers || [],
          disabledDateStrings: dateResult.disabledDateStrings || [],
          disabledDayNumbers: dateResult.disabledDayNumbers || []
        };
        var dateItems = availableDates.map(function(d) {
          return { value: d.value, translations: { en: { label: d.label } } };
        }).filter(Boolean);
        
        var formattedDateItems = [{ value: "", translations: { en: { label: "Select a date" } } }].concat(dateItems);
        updateDeliveryDateFieldOptions(formattedDateItems);
        showStaticDeliveryTime("");
        
        if (locationData && locationData.delivery) {
          window.zapietCachedLocationByLocationId[String(locationId)] = locationData;
        }
        updateSlotFromLocation(locationId, locationData);
        if (callback) callback();
      });
    })
    .catch(function(err) {
      console.error("Refetch dates for location failed:", err);
      var errorItems = [{ value: "", translations: { en: { label: "Unable to load dates for this location" } } }];
      updateDeliveryDateFieldOptions(errorItems);
      updateSlotFromLocation(locationId);
    })
    .finally(function() { zapietDatesLoading = false; });
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// Initialize location options
zapietLocationsOptions = getZapietLocationsOptions();

// Pre-fetch Zapiet delivery dates BEFORE SparkLayer initializes
(async function() {
  console.log("🚀 Starting Zapiet initialization...");
  
  if (zapietDatesLoading) {
    console.log("⏳ Zapiet dates already loading, skipping...");
    return;
  }
  zapietDatesLoading = true;
  
  window.zapietDatesReady = false;
  
  try {
    // First, fetch locations from API
    console.log("📍 Fetching Zapiet locations...");
    var locations = await fetchZapietLocations();
    if (locations && locations.length > 0) {
      window.zapietLocations = locations;
      console.log("✅ Zapiet locations loaded:", locations.length, "locations");
      console.log("✅ Location IDs:", locations.map(function(l) { return l.id; }));
      
      zapietLocationsOptions = getZapietLocationsOptions();
      console.log("✅ Updated zapietLocationsOptions:", zapietLocationsOptions);
      
      if (!window.ZAPIET_DELIVERY_LOCATION_ID && locations[0]) {
        window.ZAPIET_DELIVERY_LOCATION_ID = String(locations[0].id);
        console.log("✅ Set default location ID:", window.ZAPIET_DELIVERY_LOCATION_ID);
      }
      
      updateDeliveryLocationSelectOptions();
    } else {
      console.warn("⚠️ No locations fetched from API");
    }
    
    console.log("📦 Fetching cart...");
    const cartResponse = await fetch("/cart.js");
    if (!cartResponse.ok) {
      console.error("❌ Failed to fetch cart for Zapiet availability:", cartResponse.status);
      var cartErrorItems = [
        { value: "", translations: { en: { label: "Select a date" } } },
        { value: "", translations: { en: { label: "Unable to load dates" } } }
      ];
      updateDeliveryDateFieldOptions(cartErrorItems);
      return;
    }

    const cart = await cartResponse.json();
    console.log("✅ Cart fetched:", (cart.items && cart.items.length) || 0, "items");
    
    if (!window.ZAPIET_DELIVERY_LOCATION_ID && window.zapietLocations && window.zapietLocations[0]) {
      window.ZAPIET_DELIVERY_LOCATION_ID = String(window.zapietLocations[0].id);
      console.log("✅ Set location ID from zapietLocations:", window.ZAPIET_DELIVERY_LOCATION_ID);
    }
    const locationId = getEffectiveLocationId();
    console.log("📍 Effective Location ID:", locationId);
    console.log("📍 window.ZAPIET_DELIVERY_LOCATION_ID:", window.ZAPIET_DELIVERY_LOCATION_ID);
    console.log("📍 window.zapietLocations:", window.zapietLocations);

    if (!locationId) {
      console.error("❌ Zapiet: No location ID configured");
      updateDeliveryDateFieldOptions([
        { value: "", translations: { en: { label: "Select a date" } } },
        { value: "", translations: { en: { label: "Please configure delivery location" } } }
      ]);
      return;
    }

    console.log("🌐 Fetching Zapiet calendar rules...");
    const locationData = await fetchZapietCalendarRules(cart, locationId);
    
    if (!locationData) {
      console.error("❌ Zapiet: No location data returned");
      updateDeliveryDateFieldOptions([
        { value: "", translations: { en: { label: "Select a date" } } },
        { value: "", translations: { en: { label: "Unable to fetch availability" } } }
      ]);
      return;
    }
    
    console.log("✅ Zapiet location data received:", locationData);

    console.log("📅 Generating available dates...");
    var dateResult = generateAvailableDates(locationData);
    var availableDates = dateResult.dates || [];
    console.log("📅 Generated dates:", availableDates.length, "dates");
    
    if (!Array.isArray(availableDates)) {
      console.error("❌ generateAvailableDates did not return valid dates:", typeof availableDates);
      updateDeliveryDateFieldOptions([
        { value: "", translations: { en: { label: "Select a date" } } },
        { value: "", translations: { en: { label: "Error processing dates" } } }
      ]);
      return;
    }
    
    if (availableDates.length === 0) {
      console.warn("⚠️ No available dates generated");
      updateDeliveryDateFieldOptions([
        { value: "", translations: { en: { label: "Select a date" } } },
        { value: "", translations: { en: { label: "No available dates" } } }
      ]);
      return;
    }

    const dateItems = availableDates.map(date => {
      if (!date || typeof date.value === 'undefined' || typeof date.label === 'undefined') {
        return null;
      }
      return {
        value: date.value,
        translations: { en: { label: date.label } }
      };
    }).filter(item => item !== null);

    if (dateItems.length === 0) {
      updateDeliveryDateFieldOptions([
        { value: "", translations: { en: { label: "Select a date" } } },
        { value: "", translations: { en: { label: "No valid dates found" } } }
      ]);
      return;
    }

    var todayStr = getTodayDateString();
    var effectiveMinDate = locationData.minDate;
    if (!effectiveMinDate || effectiveMinDate < todayStr) {
      effectiveMinDate = todayStr;
    }
    window.zapietCalendarRules = {
      minDate: effectiveMinDate,
      maxDate: locationData.maxDate,
      disabled: locationData.disabled || [],
      enabledDaysOfWeek: dateResult.enabledDaysOfWeek || [],
      enabledDayNumbers: dateResult.enabledDayNumbers || [],
      disabledDateStrings: dateResult.disabledDateStrings || [],
      disabledDayNumbers: dateResult.disabledDayNumbers || []
    };
    
    var selectItems = [{ value: "", translations: { en: { label: "Select a date" } } }].concat(dateItems);
    updateDeliveryDateFieldOptions(selectItems);
    
    console.log("✅ Zapiet delivery dates updated");
    console.log("✅ Number of available dates:", dateItems.length);
    console.log("✅ Sample dates:", dateItems.slice(0, 3).map(d => d.value));
    console.log("✅ Calendar rules stored:", window.zapietCalendarRules);
    
    window.zapietDatesReady = true;
    console.log("✅ Zapiet dates ready - SparkLayer can now initialize");
    
    if (locationData && locationData.delivery) {
      window.zapietCachedLocationByLocationId = window.zapietCachedLocationByLocationId || {};
      window.zapietCachedLocationByLocationId[String(locationId)] = locationData;
    }
    
    updateSlotFromLocation(locationId, locationData);
    updateDeliveryLocationSelectOptions();
    
    if (window.initSparkLayer) {
      console.log("🚀 Initializing SparkLayer now that dates are ready...");
      window.initSparkLayer();
    }
    
    console.log("✅ Zapiet delivery dates fetch completed successfully");
  } catch (error) {
    console.error("❌ Error pre-fetching Zapiet delivery dates:", error);
    console.error("❌ Error stack:", error.stack);
    updateDeliveryDateFieldOptions([
      { value: "", translations: { en: { label: "Select a date" } } },
      { value: "", translations: { en: { label: "Error loading dates: " + error.message } } }
    ]);
    
    window.zapietDatesReady = true;
    
    if (window.initSparkLayer) {
      console.log("🚀 Initializing SparkLayer despite error...");
      window.initSparkLayer();
    }
  } finally {
    zapietDatesLoading = false;
    // Always unblock SparkLayer init (early returns may skip success/error paths)
    if (!window.zapietDatesReady) {
      window.zapietDatesReady = true;
      console.log("✅ Zapiet dates ready (finally) - SparkLayer can initialize");
      if (window.initSparkLayer && !window.sparkLayerLoaded) {
        window.initSparkLayer();
      }
    }
    console.log("🏁 Zapiet dates loading flag reset");
  }
})();
